import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

import { ILegalTransactionTalkRoomDraft } from '../../../Interfaces/ILegalTransactionTalkRoom';
import { ILegalClient } from '../../../Interfaces/ILegalClient';
import { useGetTalkRoomParticipants } from '../../../hooks/reactQuerys/LegalTransactionTalkRoom';
import {
  getLegalClientLabel,
  getTalkParticipantLabel,
  getTalkParticipantSecondaryLabel,
  getVisibleTalkParticipants,
} from './talkRoomDisplay';

interface IProps {
  open: boolean;
  onClose: () => void;
  room: ILegalTransactionTalkRoomDraft | null;
  disabled: boolean;
  onChangeParticipants: (p: any[]) => void;
  legalClients: ILegalClient[];
  invitedLegalClients: ILegalClient[];
}

export const ParticipantsDialog: React.FC<IProps> = ({
  open,
  onClose,
  room,
  invitedLegalClients,
}) => {
  const idRoom = room?.idLegalTransactionTalkRoom ?? null;
  const canLoadLive = open && !!room && (idRoom ?? 0) > 0;

  const {
    participants: liveParticipants,
    isLoadingParticipants,
    refetchParticipants,
  } = useGetTalkRoomParticipants(idRoom, canLoadLive);

  const visibleTalkParticipants = useMemo(
    () => getVisibleTalkParticipants(liveParticipants ?? []),
    [liveParticipants]
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
        Teilnehmer
        {room?.RoomName && (
          <Typography variant='body2' color='text.secondary' sx={{ marginLeft: 'auto' }}>
            Raum: <b>{room.RoomName}</b>
          </Typography>
        )}
        <Tooltip title='Aktualisieren'>
          <span>
            <IconButton
              size='small'
              onClick={() => refetchParticipants()}
              disabled={!canLoadLive}
              sx={{ ml: 0.5 }}
            >
              <RefreshIcon fontSize='small' />
            </IconButton>
          </span>
        </Tooltip>
      </DialogTitle>

      <DialogContent sx={{ display: 'grid', gap: 2, mt: 1 }}>
        {!room ? (
          <Typography>Kein Raum ausgewählt.</Typography>
        ) : (
          <>
            <Box sx={{ display: 'grid', gap: 1 }}>
              <Typography variant='subtitle2'>Über Termin eingeladen</Typography>
              <Typography variant='body2' color='text.secondary'>
                {invitedLegalClients.length} Mandant{invitedLegalClients.length === 1 ? '' : 'en'} sind über
                verknüpfte Termine informiert.
              </Typography>
              {invitedLegalClients.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {invitedLegalClients.map(legalClient => (
                    <Chip
                      key={legalClient.idLegalClient}
                      label={getLegalClientLabel(legalClient)}
                      size='small'
                      variant='outlined'
                      title={legalClient.Email ?? undefined}
                    />
                  ))}
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 0.5 }} />

            <Box sx={{ display: 'grid', gap: 1 }}>
              <Typography variant='subtitle2'>Im Talk-Raum</Typography>
              <Typography variant='body2' color='text.secondary'>
                {visibleTalkParticipants.length} sichtbare Talk-Teilnehmer live aus Nextcloud Talk.
              </Typography>
              {(idRoom ?? 0) > 0 && isLoadingParticipants ? (
                <Typography variant='body2'>Lade Teilnehmer…</Typography>
              ) : visibleTalkParticipants.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {visibleTalkParticipants.map(participant => (
                    <Chip
                      key={`${participant.actorType}-${participant.actorId}-${participant.attendeeId}`}
                      label={getTalkParticipantLabel(participant)}
                      size='small'
                      variant='filled'
                      color='primary'
                      title={getTalkParticipantSecondaryLabel(participant)}
                    />
                  ))}
                </Box>
              ) : null}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
};
